import { useState } from "react";
import { motion } from "framer-motion";
import { EnvelopeSimple, User as UserIcon, ChatCircle, CheckCircle } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <MarketingLayout>
      <SEO
        title="Contact"
        description="Get in touch with the Nestled team. Bug reports, feature requests, partnership inquiries — we read every message and reply within two business days."
        path="/contact"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "Contact Nestled",
          "url": "https://nestledapp.io/contact",
        }}
      />
      <section className="max-w-3xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Say hello</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl text-stoneink-900 font-semibold tracking-tight">
          We read every message.
        </h1>
        <p className="mt-5 text-stoneink-700 text-lg leading-relaxed">
          Bugs, feature requests, hopes, dreams — send them all our way. We try to reply within two business days.
        </p>
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        {sent ? (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-sand-300 bg-white p-10 text-center shadow-ambient"
            data-testid="contact-sent">
            <CheckCircle weight="duotone" size={56} className="mx-auto text-sage-600" />
            <h2 className="mt-5 font-heading text-3xl text-stoneink-900 font-semibold tracking-tight">Got it. Thank you.</h2>
            <p className="mt-3 text-stoneink-700">Your note landed in our inbox. We&apos;ll write back soon.</p>
            <button onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
              className="mt-6 text-terracotta-600 hover:text-terracotta-700 font-medium underline-offset-4 hover:underline" data-testid="contact-send-another">
              Send another message
            </button>
          </motion.div>
        ) : (
          <motion.form initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit}
            className="rounded-3xl border border-sand-300 bg-white p-8 sm:p-10 shadow-ambient space-y-5"
            data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Your name" testid="contact-name">
                <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
                <input
                  data-testid="contact-name-input"
                  required value={form.name} onChange={set("name")}
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                  placeholder="Maya Rodriguez"
                />
              </Field>
              <Field label="Email" testid="contact-email">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
                <input
                  data-testid="contact-email-input"
                  required type="email" value={form.email} onChange={set("email")}
                  className="w-full h-12 pl-10 pr-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                  placeholder="you@home.app"
                />
              </Field>
            </div>
            <Field label="Subject" testid="contact-subject">
              <ChatCircle size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
              <input
                data-testid="contact-subject-input"
                value={form.subject} onChange={set("subject")}
                className="w-full h-12 pl-10 pr-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
                placeholder="Bug report, feature request, kind word…"
              />
            </Field>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Message</label>
              <textarea
                data-testid="contact-message-input"
                required minLength={4} rows={7} value={form.message} onChange={set("message")}
                className="mt-2 w-full p-4 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none resize-y"
                placeholder="Tell us what&apos;s on your mind…"
              />
            </div>
            <div className="flex justify-end">
              <button
                data-testid="contact-submit-btn"
                type="submit" disabled={busy}
                className="h-12 px-7 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium disabled:opacity-60 active:scale-[0.98]"
              >
                {busy ? "Sending…" : "Send message"}
              </button>
            </div>
          </motion.form>
        )}
        <p className="mt-6 text-center text-sm text-stoneink-500">
          Prefer plain email? Write us at <a href="mailto:contact@nestledapp.io" className="text-terracotta-600 hover:text-terracotta-700">contact@nestledapp.io</a>
        </p>
      </section>
    </MarketingLayout>
  );
}

const Field = ({ label, children, testid }) => (
  <div data-testid={`${testid}-field`}>
    <label className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">{label}</label>
    <div className="mt-2 relative">{children}</div>
  </div>
);
